/* eslint-disable no-nested-ternary */
import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { Avatar, ImageIcon } from '@saleshandy/design-system';
import { DotsVertical, FacebookBw, LinkedinBw } from '@saleshandy/icons';
import Icon from '../../../../../../../../shared/design-system/components/atoms/icon/icon';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import { getValidUrl } from '../helpers/prospect-attributes';
import { NogenderAvatar } from '../../../../../../../../shared/svg/no-gender-avatar';
import TwitterIcon from '../../../../../../../../shared/svg/twitter-icon';
import { OverlayTooltip } from '../../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { PhoneSlash } from '../../../../../../../../shared/svg/phone-slash';

export type ProspectProfileHeaderBlockProps = {
  item: any;
  dropDownActions?: React.ReactNode;
  /** Defaults to `prospect-profile-dropdown`; pass per-instance id when multiple headers mount. */
  dropdownId?: string;
  /** When provided, renders a "Do Not Call" icon button instead of the kebab dropdown. */
  onDoNotContactClick?: () => void;
  profileHeaderFromDialer?: boolean;
  isAgencyUser?: boolean;
};

/**
 * Shared header: avatar, name, optional kebab dropdown (or DotsVertical), job/company/website, social links.
 * Matches prospect-profile-section header row (single prospect card).
 */
export const ProspectProfileHeaderBlock: React.FC<ProspectProfileHeaderBlockProps> = ({
  item,
  dropDownActions,
  dropdownId = 'prospect-profile-dropdown',
  onDoNotContactClick,
  profileHeaderFromDialer = false,
  isAgencyUser = false,
}) => {
  const jobTitle =
    item?.jobTitle ||
    item?.attributes?.find(
      (attr: any) => attr?.fieldRef?.label?.trim() === 'Job Title',
    )?.attributeValue ||
    item?.prospect?.attributes?.find(
      (attr: any) => attr?.fieldRef?.label?.trim() === 'Job Title',
    )?.attributeValue;
  const company =
    item?.company ||
    item?.attributes?.find(
      (attr: any) => attr?.fieldRef?.label?.trim() === 'Company',
    )?.attributeValue ||
    item?.prospect?.attributes?.find(
      (attr: any) => attr?.fieldRef?.label?.trim() === 'Company',
    )?.attributeValue;
  const firstName =
    item?.firstName ||
    item?.prospect?.attributes?.find(
      (attr: any) => attr?.fieldRef?.label?.trim() === 'First Name',
    )?.attributeValue;
  const lastName =
    item?.lastName ||
    item?.prospect?.attributes?.find(
      (attr: any) => attr?.fieldRef?.label?.trim() === 'Last Name',
    )?.attributeValue;
  const profilePicture = item?.profilePicture || item?.prospect?.profilePicture;

  const attributes = item?.attributes || item?.prospect?.attributes;

  return (
    <div className="prospect-profile-section-header">
      {profilePicture ? (
        <ImageIcon
          src={profilePicture}
          alt="profile-picture"
          width={64}
          height={64}
          className="prospect-profile-picture"
        />
      ) : firstName || lastName ? (
        <Avatar
          firstName={firstName || 'N'}
          lastName={lastName || 'N'}
          size="lg"
          className="cs-avatar"
        />
      ) : (
        <div className="avatar-icon cs-avatar">
          <NogenderAvatar width={64} height={64} />
        </div>
      )}
      <div className="prospect-profile-section-header-user-detail">
        <div className="prospect-profile-section-header-user-detail-icon-name">
          <div
            className={`prospect-profile-section-header-user-name ${
              jobTitle || company ? 'job-title-prospect' : ''
            }`}
          >
            {firstName || lastName ? (
              <div
                className={`d-flex align-items-center gap-1 prospect-profile-header-name ${
                  item?.isNumberInCallDnc && profileHeaderFromDialer
                    ? 'prospect-profile-header-name-dnc'
                    : onDoNotContactClick
                    ? 'prospect-profile-header-name-dnc-btn'
                    : ''
                }`}
              >
                <TruncatedTextWithAutoTooltip
                  text={`${firstName ?? ''} ${lastName ?? ''}`.trim()}
                  className="semibold-4 gray-txt-10"
                />
                {item?.isNumberInCallDnc && profileHeaderFromDialer && (
                  <div className="do-not-contact-text">Do Not Call</div>
                )}
              </div>
            ) : (
              <span className="no-name-text">(No Name)</span>
            )}

            {onDoNotContactClick && !item?.isNumberInCallDnc && !isAgencyUser && (
              <OverlayTooltip text="Do Not Call">
                <div
                  className="prospect-profile-dnc-btn"
                  {...accessibleOnClick(onDoNotContactClick)}
                >
                  <PhoneSlash width={16} height={16} />
                </div>
              </OverlayTooltip>
            )}

            {!profileHeaderFromDialer &&
              (dropDownActions ? (
                <Dropdown bsPrefix="dropdown-custom" drop="down">
                  <Dropdown.Toggle
                    bsPrefix="dropdown-toggle-custom"
                    variant="dropdown-custom"
                    id={dropdownId}
                  >
                    <Icon
                      identifier="more-vertical-alt"
                      className="regular-4 gray-txt-15 icon-hover"
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu bsPrefix="dropdown-menu-custom">
                    {dropDownActions}
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <DotsVertical />
              ))}
          </div>
          <span className="prospect-profile-section-header-user-designation">
            {(() => {
              const website =
                item?.website ||
                attributes?.find(
                  (attr: any) =>
                    attr?.fieldRef?.label?.trim() === 'Company Website',
                )?.attributeValue;

              if (jobTitle && company) {
                return (
                  <div className="d-flex flex-column w-100">
                    <div className="d-flex align-items-center gap-8 job-title-prospect">
                      <TruncatedTextWithAutoTooltip
                        text={jobTitle}
                        className="prospect-job-title"
                      />
                    </div>{' '}
                    <span className="d-flex align-items-center gap-4">
                      {website || getValidUrl(website) ? (
                        <a
                          href={getValidUrl(website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex w-100"
                        >
                          <TruncatedTextWithAutoTooltip
                            text={company}
                            className={`prospect-company-name ${
                              website ? 'company-name-website' : ''
                            }`}
                          />
                        </a>
                      ) : (
                        <TruncatedTextWithAutoTooltip
                          text={company}
                          className="prospect-company-name"
                        />
                      )}
                    </span>
                  </div>
                );
              }
              if (jobTitle) {
                return (
                  <div className="d-flex align-items-center w-100">
                    <TruncatedTextWithAutoTooltip
                      text={jobTitle}
                      className="prospect-job-title"
                    />
                  </div>
                );
              }

              if (company) {
                return (
                  <div className="d-flex align-items-center gap-4 w-100">
                    {website || getValidUrl(website) ? (
                      <a
                        href={getValidUrl(website)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex w-100"
                      >
                        <TruncatedTextWithAutoTooltip
                          text={company}
                          className={`prospect-company-name ${
                            website ? 'company-name-website' : ''
                          }`}
                        />
                      </a>
                    ) : (
                      <TruncatedTextWithAutoTooltip
                        text={company}
                        className="prospect-company-name"
                      />
                    )}
                  </div>
                );
              }

              return '';
            })()}
          </span>
        </div>
        <div className="prospect-profile-section-header-user-social-media">
          {attributes
            ?.filter(
              (attr: any) =>
                attr?.attributeValue &&
                ['Facebook', 'LinkedIn', 'Twitter'].includes(
                  attr?.fieldRef?.label,
                ),
            )
            ?.map((attr: any) => {
              switch (attr?.fieldRef?.label) {
                case 'Facebook':
                  return (
                    <a
                      href={getValidUrl(attr?.attributeValue)}
                      key={attr?.id}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FacebookBw
                        className="facebook-icon"
                        width={20}
                        height={20}
                      />
                    </a>
                  );
                case 'LinkedIn':
                  return (
                    <a
                      href={getValidUrl(attr?.attributeValue)}
                      key={attr?.id}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedinBw
                        className="linkedin-icon"
                        width={20}
                        height={20}
                      />
                    </a>
                  );
                case 'Twitter':
                  return (
                    <a
                      href={getValidUrl(attr?.attributeValue)}
                      key={attr?.id}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <TwitterIcon
                        className="twitter-icon"
                        width={20}
                        height={20}
                      />
                    </a>
                  );
                default:
                  return null;
              }
            })}
        </div>
      </div>
    </div>
  );
};

export default ProspectProfileHeaderBlock;
